import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export interface DiagnosticSubmission {
  name: string;
  company: string;
  email: string;
  countryCode: string;
  whatsapp: string;
  totalScore: number;
  answers: Record<number, number>;
}

export async function saveDiagnosticLead(data: DiagnosticSubmission) {
  const { error } = await supabase.from("diagnostic_leads").insert({
    name: data.name,
    email: data.email,
    country_code: data.countryCode,
    whatsapp: data.whatsapp || null,
    total_score: data.totalScore,
    answers: data.answers,
  });

  if (error) {
    logger.error("Error saving diagnostic lead:", error);
    throw error;
  }

  // Track Meta Pixel custom conversion event
  if (typeof window !== 'undefined' && (window as any).fbq) {
    (window as any).fbq('trackCustom', 'CompleteTest', {
      content_name: 'Test Diagnóstico Capital de Marca',
      score: data.totalScore
    });
  }

  // Send email notification
  try {
    const { error: notificationError } = await supabase.functions.invoke('send-lead-notification', {
      body: {
        name: data.name,
        company: data.company,
        email: data.email,
        countryCode: data.countryCode,
        whatsapp: data.whatsapp,
        totalScore: data.totalScore,
        answers: data.answers,
      },
    });

    if (notificationError) {
      logger.error("Error sending lead notification email:", notificationError);
      // Don't throw - email failure shouldn't block the lead submission
    }
  } catch (emailError) {
    logger.error("Failed to invoke send-lead-notification:", emailError);
    // Don't throw - email failure shouldn't block the lead submission
  }

  return true;
}

export interface DiagnosticStats {
  totalLeads: number;
  averageScore: number;
  scoreDistribution: {
    low: number; // 0-30
    medium: number; // 40-70
    high: number; // 80-100
  };
  recentLeads: Array<{
    id: string;
    name: string;
    email: string;
    totalScore: number;
    createdAt: string;
  }>;
}

export async function getDiagnosticStats(): Promise<DiagnosticStats> {
  const { data, error } = await supabase.rpc("get_diagnostic_leads_admin");

  if (error) {
    logger.error("Error fetching diagnostic stats:", error);
    throw error;
  }


  const leads = data || [];
  const totalLeads = leads.length;
  const averageScore = totalLeads > 0 
    ? Math.round(leads.reduce((sum, lead) => sum + lead.total_score, 0) / totalLeads)
    : 0;

  const scoreDistribution = {
    low: leads.filter(l => l.total_score <= 30).length,
    medium: leads.filter(l => l.total_score > 30 && l.total_score <= 70).length,
    high: leads.filter(l => l.total_score > 70).length,
  };

  const recentLeads = leads.slice(0, 10).map(lead => ({
    id: lead.id,
    name: lead.name,
    email: lead.email,
    totalScore: lead.total_score,
    createdAt: lead.created_at,
  }));

  return {
    totalLeads,
    averageScore,
    scoreDistribution,
    recentLeads,
  };
}
